import endpoints from 'constants/endpoints';
import { REFRESH_USER } from 'constants/types';
import { getCompany } from 'helpers/getCompanyVariables';
import { safePostCall } from 'helpers/firebaseSetup';
import { parseZohoJsonOutput } from 'helpers/zoho/response-parsers';
import {
  getSuncarePhase,
  SUNCARE_PHASES,
  SUNCARE_ZOHO_FIELDS
} from 'helpers/suncare/suncare';
import { refreshUser } from './user';
import { showAlert } from './site';

const ENDPOINT_NOT_REGISTERED_MESSAGE =
  'The Suncare update endpoint is not available. Please contact support — the backend handler may need to be registered.';

function extractErrorMessage(response, error) {
  const output = response?.data?.details?.output;
  if (output != null && output !== '') {
    const { message } = parseZohoJsonOutput(output);
    if (message) return message;

    if (typeof output === 'string') {
      try {
        const nested = JSON.parse(output);
        if (nested?.message) return nested.message;
        if (nested?.output) {
          const parsedNested = parseZohoJsonOutput(nested.output);
          if (parsedNested.message) return parsedNested.message;
        }
      } catch {
        if (output.trim()) return output;
      }
    }
  }

  const status = error?.response?.status ?? response?.status;
  if (status === 500) {
    return ENDPOINT_NOT_REGISTERED_MESSAGE;
  }

  return error?.message || 'Something went wrong. Please try again later.';
}

export const sendSuncareContractSent = () => async (dispatch, getState) => {
  if (getCompany() !== 'sunshine') {
    return { ok: false };
  }

  const user = getState().user.zohoData;

  if (user?.[SUNCARE_ZOHO_FIELDS.ValidCertificate]) {
    dispatch(
      showAlert({
        message: 'No Suncare steps are pending.',
        code: 'info'
      })
    );
    return { ok: true, skipped: true };
  }

  if (user?.[SUNCARE_ZOHO_FIELDS.ContractSent]) {
    await dispatch(refreshUser());
    return { ok: true, skipped: true };
  }

  if (!endpoints.zoho.suncareUpdateAgentInformation) {
    dispatch(
      showAlert({
        message: ENDPOINT_NOT_REGISTERED_MESSAGE,
        code: 'error'
      })
    );
    return { ok: false };
  }

  try {
    const response = await safePostCall(endpoints.awsProxy.safePost, {
      zohoId: getState().user.zohoData.id,
      endpoint: endpoints.zoho.suncareUpdateAgentInformation,
      suncareUpdateIdentifier: SUNCARE_ZOHO_FIELDS.ContractSent,
      isZohoRequest: true
    });

    if (!response?.data?.details) {
      dispatch(
        showAlert({
          message: ENDPOINT_NOT_REGISTERED_MESSAGE,
          code: 'error'
        })
      );
      return { ok: false };
    }

    const { ok, message } = parseZohoJsonOutput(response.data.details.output);

    if (!ok) {
      dispatch(
        showAlert({
          message: message || 'Failed to record contract status.',
          code: 'error'
        })
      );
      return { ok: false };
    }

    dispatch(
      showAlert({
        message: message || 'Contract status recorded successfully.',
        code: 'success'
      })
    );

    dispatch({
      type: REFRESH_USER,
      payload: {
        ...getState().user.zohoData,
        [SUNCARE_ZOHO_FIELDS.ContractSent]: true
      }
    });

    await dispatch(refreshUser());

    return { ok: true };
  } catch (error) {
    console.error(error);
    dispatch(
      showAlert({
        message: extractErrorMessage(null, error),
        code: 'error'
      })
    );
    return { ok: false };
  }
};

export function shouldUseSuncareSendContract(requiredActions, user) {
  return (
    getSuncarePhase(user) === SUNCARE_PHASES.signContract &&
    requiredActions?.some(action => action.type === 'suncareContractSigning')
  );
}
